import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { publicAPI, testimonialAPI, subscriptionTierAPI, programImageAPI } from '@/lib/api';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { 
  BookOpen, ArrowRight, CheckCircle, Clock, Trophy, 
  Users, FileText, Play, MessageCircle, Award, DollarSign,
  Youtube, ChevronRight, Phone, Mail, MapPin, Star, Quote, PlayCircle
} from 'lucide-react';

export default function LandingPage() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [pricingTiers, setPricingTiers] = useState([]);
  const [programImages, setProgramImages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [settingsRes, statsRes, testimonialsRes, tiersRes, programImagesRes] = await Promise.all([
        publicAPI.getLandingSettings(),
        publicAPI.getStats(),
        testimonialAPI.getPublic({ limit: 8 }).catch(() => ({ data: [] })),
        subscriptionTierAPI.getPublic().catch(() => ({ data: [] })),
        programImageAPI.getPublic().catch(() => ({ data: [] }))
      ]);
      setSettings(settingsRes.data);
      setStats(statsRes.data);
      setTestimonials(testimonialsRes.data || []);
      setPricingTiers(tiersRes.data || []);
      setProgramImages(programImagesRes.data || []);
    } catch (error) {
      console.error('Failed to load landing data:', error);
      setSettings(getDefaultSettings());
      setStats({ formatted: { questions: '10,000+', users: '3,000+', pass_rate: 'Ribuan Alumni' } });
      setTestimonials([]);
      setPricingTiers([]);
      setProgramImages([]);
    } finally {
      setLoading(false);
    }
  };

  const getDefaultSettings = () => ({
    hero_title: "Bimbel My Tentor",
    hero_subtitle: "Selamat Datang di",
    hero_description: "Platform Try Out Online dengan Tentor berpengalaman untuk UTBK-SNBT, TNI, POLRI, KEDINASAN, UNHAN, CPNS, dan SMA Favorit/Unggulan.",
    hero_image: "https://images.unsplash.com/photo-1629360021730-3d258452c425?auto=format&fit=crop&q=80&w=600",
    hero_badge_text: "Platform Try Out Online Terpercaya",
    stats_pass_rate_label: "Siswa Lulus",
    stats_users_label: "Siswa Aktif",
    stats_questions_label: "Soal Latihan",
    stats_experience_label: "Tahun Pengalaman",
    stats_experience_value: "15+",
    commitment_title: "Komitmen kami untuk kesuksesan Anda",
    commitments: [
      { icon: "Users", title: "Tentor Terbaik", desc: "Didukung tentor berkualitas dengan pengalaman lebih dari 15 tahun" },
      { icon: "DollarSign", title: "Harga Terjangkau", desc: "Paket belajar dengan harga sangat terjangkau, mulai dari gratis" },
      { icon: "FileText", title: "Soal Berkualitas", desc: "Ribuan soal dengan pembahasan lengkap dan detail" }
    ],
    whatsapp_number: "6281262017001",
    whatsapp_message: "Halo Admin MyTentor, saya ingin bergabung di grup belajar",
    youtube_channel: "https://www.youtube.com/@mytentorofficial",
    contact_phone: "+62 812 6201 7001",
    contact_email: "mytentor2017@gmail.com",
    contact_address: "Jl. Gaperta No. 218 A Medan Helvetia"
  });

  const s = settings || getDefaultSettings();
  const dynamicStats = stats?.formatted || { questions: '10,000+', users: '3,000+', pass_rate: 'Ribuan Alumni' };

  const commitments = [
    { icon: Users, title: 'Tentor Terbaik', desc: 'Didukung tentor berkualitas dengan pengalaman lebih dari 15 tahun' },
    { icon: DollarSign, title: 'Harga Terjangkau', desc: 'Paket belajar dengan harga sangat terjangkau, mulai dari gratis' },
    { icon: FileText, title: 'Soal Berkualitas', desc: 'Ribuan soal dengan pembahasan lengkap dan detail' },
  ];

  const tryouts = [
    { title: 'Try Out UTBK-SNBT 2025', questions: 100, duration: 120, price: 0 },
    { title: 'Try Out TWK CPNS', questions: 50, duration: 60, price: 25000 },
    { title: 'Try Out Psikotes Polri', questions: 80, duration: 90, price: 35000 },
  ];

  // Default pricing packages (fallback if database is empty)
  const defaultPricingPackages = [
    { 
      display_name: 'GRATIS', 
      price: 0, 
      description: '',
      features: ['Try Out Terbatas', 'Akses Soal Dasar', 'Ranking Nasional', 'Pembahasan Dasar'],
      popular: false,
      cta: 'Mulai Gratis'
    },
    { 
      display_name: 'Paket Basic', 
      price: 5000, 
      description: 'per paket',
      features: ['1x Try Out Lengkap', 'Pembahasan Detail', 'Analisis Jawaban'],
      popular: false,
      cta: 'Beli Paket'
    },
    { 
      display_name: 'Paket Silver', 
      price: 15000, 
      description: '3 Try Out',
      features: ['3x Try Out Pilihan', 'Pembahasan Lengkap', 'Konsultasi WhatsApp', 'Sertifikat Digital'],
      popular: true,
      cta: 'Pilih Silver'
    },
    { 
      display_name: 'Paket Gold', 
      price: 25000, 
      description: '5 Try Out',
      features: ['5x Try Out Pilihan', 'Semua Materi', 'Konsultasi WhatsApp', 'Akses Premium'],
      popular: false,
      cta: 'Pilih Gold'
    }
  ];

  // Use database pricing if available, otherwise use defaults
  const displayPricingPackages = pricingTiers.length > 0 
    ? pricingTiers.map(tier => ({
        ...tier,
        popular: tier.name === 'silver' || tier.name === 'gold',
        cta: tier.price === 0 ? 'Mulai Gratis' : `Pilih ${tier.display_name}`
      }))
    : defaultPricingPackages;

  // YouTube videos from MyTentor channel
  const youtubeVideos = [
    { 
      id: 'd5hs5soGQ4Q', 
      title: 'Pengenalan My Tentor', 
      description: 'Video perkenalan Bimbel My Tentor - Channel Pembelajaran Nomor 1 Se-Indonesia'
    }
  ];

  const videos = [
    { title: 'Tips Mengerjakan Soal TPS', views: '12,000', duration: '15:30', image: 'https://images.pexels.com/photos/4145153/pexels-photo-4145153.jpeg?auto=compress&cs=tinysrgb&w=400' },
    { title: 'Strategi TWK untuk CPNS', views: '8,500', duration: '20:45', image: 'https://images.pexels.com/photos/4145190/pexels-photo-4145190.jpeg?auto=compress&cs=tinysrgb&w=400' },
    { title: 'Persiapan Psikotes TNI/Polri', views: '15,000', duration: '18:20', image: 'https://images.pexels.com/photos/5212320/pexels-photo-5212320.jpeg?auto=compress&cs=tinysrgb&w=400' },
  ];

  // Default programs (fallback if database is empty)
  const defaultPrograms = [
    { program_id: 'UTBK-SNBT', name: 'UTBK-SNBT', description: 'Persiapan tes masuk Perguruan Tinggi Negeri', color: '#4472C4', image_url: 'https://images.pexels.com/photos/267885/pexels-photo-267885.jpeg?auto=compress&cs=tinysrgb&w=400' },
    { program_id: 'POLRI', name: 'Tes Polri', description: 'Tamtama, Bintara, Akpol, dan SIPSS', color: '#1E3A5F', image_url: 'https://images.pexels.com/photos/8112181/pexels-photo-8112181.jpeg?auto=compress&cs=tinysrgb&w=400' },
    { program_id: 'TNI', name: 'Tes TNI', description: 'AD, AL, AU (Tamtama, Bintara, Akmil)', color: '#2D5A27', image_url: 'https://images.pexels.com/photos/8112181/pexels-photo-8112181.jpeg?auto=compress&cs=tinysrgb&w=400' },
    { program_id: 'KEDINASAN', name: 'Sekolah Kedinasan', description: 'TWK, TIU, TKP untuk sekolah kedinasan', color: '#8B4513', image_url: 'https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=400' },
    { program_id: 'CPNS', name: 'CPNS', description: 'TWK, TIU, TKP untuk calon ASN', color: '#6B21A8', image_url: 'https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=400' },
    { program_id: 'SMA', name: 'SMA Favorit', description: 'Persiapan tes masuk SMA unggulan', color: '#DC2626', image_url: 'https://images.pexels.com/photos/5212345/pexels-photo-5212345.jpeg?auto=compress&cs=tinysrgb&w=400' },
  ];

  // Use database program images if available, otherwise use defaults
  const displayPrograms = programImages.length > 0 ? programImages : defaultPrograms;

  const ebooks = [
    { title: 'Panduan Lengkap UTBK-SNBT 2025', pages: 250, price: 50000 },
    { title: 'Strategi Lolos TNI/Polri', pages: 180, price: 45000 },
    { title: 'Master TWK Kedinasan', pages: 150, price: 35000 },
    { title: 'Tips & Trik CPNS', pages: 120, price: 0 },
  ];

  // Default testimonials (fallback if database is empty)
  const defaultTestimonials = [
    {
      name: "Ahmad Rizky",
      role: "Lulus UTBK-SNBT 2025",
      institution: "Universitas Indonesia",
      image_url: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face",
      rating: 5,
      quote: "Berkat latihan soal di MyTentor, saya bisa lolos ke UI! Soal-soalnya mirip dengan yang keluar di ujian dan pembahasannya sangat membantu."
    },
    {
      name: "Putri Ayu Lestari",
      role: "Lulus Bintara Polri 2024",
      institution: "Polda Sumatera Utara",
      image_url: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face",
      rating: 5,
      quote: "Psikotes dan akademiknya lengkap banget! Tentor di MyTentor sabar menjelaskan sampai saya paham. Sekarang sudah jadi anggota Polri."
    },
    {
      name: "Budi Santoso",
      role: "Lulus CPNS 2024",
      institution: "Kementerian Keuangan",
      image_url: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face",
      rating: 5,
      quote: "TWK, TIU, TKP semuanya ada di MyTentor dengan pembahasan detail. Alhamdulillah sekarang sudah jadi PNS di Kemenkeu!"
    },
    {
      name: "Siti Nurhaliza",
      role: "Lulus Sekolah Kedinasan 2024",
      institution: "STAN",
      image_url: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face",
      rating: 5,
      quote: "Try out di MyTentor bikin saya terbiasa dengan soal-soal SKD. Ranking sistem juga memotivasi untuk terus belajar. Terima kasih MyTentor!"
    }
  ];

  // Use database testimonials if available, otherwise use defaults
  const displayTestimonials = testimonials.length > 0 ? testimonials : defaultTestimonials;

  const formatPrice = (price) => {
    if (price === 0) return 'GRATIS';
    return `Rp ${price.toLocaleString('id-ID')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#4472C4] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-white/30 border-t-white rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white/80">Memuat...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header with Mobile Navigation */}
      <Header showStats={true} stats={dynamicStats} />

      {/* Hero Section */}
      <section className="bg-[#4472C4] text-white">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div className="space-y-6">
              <Badge className="bg-[#CCFF00] text-slate-900 hover:bg-[#CCFF00]/90 border-0 font-semibold">
                {s.hero_badge_text}
              </Badge>
              
              <div>
                <p className="text-lg opacity-90">{s.hero_subtitle}</p>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-tight">
                  {s.hero_title}
                  <span className="block w-32 h-1.5 bg-[#CCFF00] mt-3 rounded-full"></span>
                </h1>
                <p className="text-sm opacity-80 mt-2 tracking-wide uppercase">Solution For Students</p>
              </div>
              
              <p className="text-lg opacity-90 leading-relaxed max-w-lg">
                {s.hero_description}
              </p>
              
              <div className="flex flex-wrap gap-4 pt-2">
                <Link to="/register">
                  <Button className="bg-[#CCFF00] hover:bg-[#b8e600] text-slate-900 font-semibold rounded-full px-8 py-6 text-base shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5" data-testid="cta-register-btn">
                    Mulai Belajar Gratis
                  </Button>
                </Link>
                <a href="#programs">
                  <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-slate-900 rounded-full px-8 py-6 text-base transition-all" data-testid="cta-programs-btn">
                    Lihat Program
                  </Button>
                </a>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1625111381887-458fce74a923?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwyfHxpbmRvbmVzaWFuJTIwaGlnaCUyMHNjaG9vbCUyMHN0dWRlbnRzJTIwc3R1ZHlpbmclMjBoYXBweXxlbnwwfHx8fDE3ODUxNjk0Njl8MA&ixlib=rb-4.1.0&q=85" 
                alt="Students studying" 
                className="rounded-2xl shadow-2xl w-full aspect-[4/3] object-cover border border-white/10"
              />
              <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm rounded-xl px-5 py-3 shadow-lg flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-slate-900">{dynamicStats.pass_rate}</div>
                  <div className="text-xs text-slate-500">{s.stats_pass_rate_label}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#4472C4] border-t border-white/20">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center text-white">
            <div>
              <div className="text-3xl md:text-4xl font-bold">{dynamicStats.pass_rate}</div>
              <div className="text-sm opacity-80">{s.stats_pass_rate_label}</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold">{dynamicStats.users}</div>
              <div className="text-sm opacity-80">{s.stats_users_label}</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold">{dynamicStats.questions}</div>
              <div className="text-sm opacity-80">{s.stats_questions_label}</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold">{s.stats_experience_value}</div>
              <div className="text-sm opacity-80">{s.stats_experience_label}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Tentang Kami - Komitmen */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-[#4472C4]/10 text-[#4472C4] hover:bg-[#4472C4]/20 mb-4">Tentang Kami</Badge>
            <h2 className="text-3xl font-bold text-slate-900">{s.commitment_title}</h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {commitments.map((item) => (
              <div key={item.title} className="text-center p-6 bg-white rounded-xl border border-slate-200 hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 bg-[#4472C4]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-7 h-7 text-[#4472C4]" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-500 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Try Out Online Section */}
      <section id="try-out" className="py-16 px-4 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Left - Info */}
            <div>
              <Badge className="bg-[#4472C4]/10 text-[#4472C4] hover:bg-[#4472C4]/20 mb-4">Try Out Online</Badge>
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Latihan soal berkualitas</h2>
              <p className="text-slate-600 mb-6">
                Ribuan soal dengan pembahasan lengkap, timer otomatis, dan sistem scoring akurat
              </p>
              
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#4472C4]/10 rounded-lg flex items-center justify-center shrink-0">
                    <FileText className="w-5 h-5 text-[#4472C4]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">{dynamicStats.questions} Soal Latihan</h4>
                    <p className="text-sm text-slate-500">Bank soal lengkap untuk semua program ujian</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#4472C4]/10 rounded-lg flex items-center justify-center shrink-0">
                    <CheckCircle className="w-5 h-5 text-[#4472C4]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Pembahasan Lengkap</h4>
                    <p className="text-sm text-slate-500">Setiap soal dilengkapi pembahasan detail</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#4472C4]/10 rounded-lg flex items-center justify-center shrink-0">
                    <Trophy className="w-5 h-5 text-[#4472C4]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Leaderboard & Ranking</h4>
                    <p className="text-sm text-slate-500">Bandingkan skor dengan siswa lainnya</p>
                  </div>
                </div>
              </div>
              
              <Link to={user ? '/dashboard' : '/register'}>
                <Button className="bg-[#FF6B35] hover:bg-[#e55a2a] text-white rounded-lg">
                  Mulai Try Out Sekarang
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            
            {/* Right - Try Out Cards */}
            <div className="space-y-4">
              {tryouts.map((to) => (
                <div key={to.title} className="flex items-center justify-between p-4 bg-white rounded-xl border border-slate-200 hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#4472C4]/10 rounded-lg flex items-center justify-center">
                      <FileText className="w-5 h-5 text-[#4472C4]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">{to.title}</h4>
                      <p className="text-sm text-slate-500">{to.questions} soal - {to.duration} menit</p>
                    </div>
                  </div>
                  <Badge className={to.price === 0 ? 'bg-green-100 text-green-700 hover:bg-green-100' : 'bg-[#4472C4] text-white hover:bg-[#4472C4]'}>
                    {formatPrice(to.price)}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Video Pembelajaran */}
      <section id="video" className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-[#4472C4]/10 text-[#4472C4] hover:bg-[#4472C4]/20 mb-4">Video Pembelajaran</Badge>
            <h2 className="text-3xl font-bold text-slate-900 mb-2">Belajar lewat video</h2>
            <p className="text-slate-500">Materi pembelajaran dalam format video yang mudah dipahami</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {videos.map((video) => (
              <a 
                key={video.title}
                href={s.youtube_channel}
                target="_blank"
                rel="noopener noreferrer"
                className="group"
              >
                <div className="relative rounded-xl overflow-hidden mb-3">
                  <img src={video.image} alt={video.title} className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300" />
                  <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-14 h-14 bg-white/90 rounded-full flex items-center justify-center">
                      <Play className="w-6 h-6 text-[#4472C4] ml-1" />
                    </div>
                  </div>
                  <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                    {video.duration}
                  </div>
                </div>
                <h3 className="font-semibold text-slate-900 group-hover:text-[#4472C4] transition-colors">{video.title}</h3>
                <p className="text-sm text-slate-500">{video.views} views</p>
              </a>
            ))}
          </div>
          
          <div className="text-center">
            <a href={s.youtube_channel} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="rounded-lg">
                Lihat Semua Video di YouTube
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Program Persiapan - Dynamic from Database */}
      <section id="programs" className="py-16 px-4 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-[#4472C4]/10 text-[#4472C4] hover:bg-[#4472C4]/20 mb-4">Program Persiapan</Badge>
            <h2 className="text-3xl font-bold text-slate-900">Pilih program yang sesuai</h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayPrograms.map((prog, idx) => (
              <Link
                key={prog.program_id || prog.id || idx}
                to={user ? '/dashboard' : '/register'}
                className="group relative rounded-xl overflow-hidden bg-white border border-slate-200 hover:shadow-xl transition-all"
                data-testid={`program-card-${prog.program_id || idx}`}
              >
                <div className="relative h-40">
                  {prog.image_url ? (
                    <img 
                      src={prog.image_url} 
                      alt={prog.name} 
                      className="w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  {/* Fallback color background */}
                  <div 
                    className={`absolute inset-0 flex items-center justify-center ${prog.image_url ? 'hidden' : 'flex'}`}
                    style={{ backgroundColor: prog.color || '#4472C4' }}
                  >
                    <BookOpen className="w-12 h-12 text-white/50" />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                  <Badge 
                    className="absolute top-3 left-3 text-white text-xs hover:opacity-90"
                    style={{ backgroundColor: prog.color || '#FF6B35' }}
                  >
                    {prog.program_id || 'Program'}
                  </Badge>
                </div>
                <div className="p-4">
                  <h3 className="font-bold text-slate-900 mb-1">{prog.name}</h3>
                  <p className="text-sm text-slate-500 mb-3 line-clamp-2">{prog.description}</p>
                  <div className="flex items-center text-[#4472C4] text-sm font-medium group-hover:gap-2 transition-all">
                    Lihat Paket <ChevronRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section - Transparent Pricing */}
      <section id="pricing" className="py-16 px-4 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-green-100 text-green-700 hover:bg-green-200 mb-4">Harga Transparan</Badge>
            <h2 className="text-3xl font-bold text-slate-900 mb-2">Pilih Paket Sesuai Kebutuhan</h2>
            <p className="text-slate-500">Harga jelas tanpa biaya tersembunyi. Tidak perlu tanya via WhatsApp!</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {displayPricingPackages.map((pkg, idx) => (
              <div 
                key={pkg.id || idx}
                className={`relative bg-white rounded-2xl border-2 p-6 transition-all hover:shadow-xl ${
                  pkg.popular ? 'border-[#4472C4] shadow-lg scale-105' : 'border-slate-200 hover:border-[#4472C4]/50'
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-[#4472C4] text-white px-4">Paling Populer</Badge>
                  </div>
                )}
                
                <div className="text-center mb-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{pkg.display_name}</h3>
                  <div className="flex items-baseline justify-center gap-1">
                    {pkg.price === 0 ? (
                      <span className="text-4xl font-bold text-green-600">GRATIS</span>
                    ) : (
                      <>
                        <span className="text-sm text-slate-400">Rp</span>
                        <span className="text-4xl font-bold text-slate-900">{pkg.price.toLocaleString('id-ID')}</span>
                      </>
                    )}
                  </div>
                  {pkg.description && (
                    <p className="text-sm text-slate-500 mt-1">{pkg.description}</p>
                  )}
                </div>
                
                <ul className="space-y-3 mb-6">
                  {(pkg.features || []).map((feature, fidx) => (
                    <li key={fidx} className="flex items-start gap-2 text-sm">
                      <CheckCircle className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                      <span className="text-slate-600">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <Link to={user ? '/packages' : '/register'}>
                  <Button 
                    className={`w-full ${
                      pkg.popular 
                        ? 'bg-[#4472C4] hover:bg-[#3861a8] text-white' 
                        : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                    }`}
                  >
                    {pkg.cta}
                  </Button>
                </Link>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-8">
            <p className="text-slate-500 text-sm">
              * Harga dapat berubah sewaktu-waktu. Lihat detail lengkap di{' '}
              <Link to="/packages" className="text-[#4472C4] font-medium hover:underline">halaman Paket</Link>
            </p>
          </div>
        </div>
      </section>

      {/* Video Testimoni Section */}
      <section id="video-testimoni" className="py-16 px-4 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-red-500/20 text-red-400 hover:bg-red-500/30 mb-4">Video</Badge>
            <h2 className="text-3xl font-bold text-white mb-2">Kenali Lebih Dekat My Tentor</h2>
            <p className="text-slate-400">Tonton video pengenalan dan testimoni alumni kami</p>
          </div>
          
          <div className="max-w-3xl mx-auto">
            {/* Main Video Embed */}
            <div className="relative aspect-video rounded-2xl overflow-hidden shadow-2xl border border-slate-700">
              <iframe
                className="w-full h-full"
                src="https://www.youtube.com/embed/d5hs5soGQ4Q?rel=0"
                title="Pengenalan My Tentor"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            
            {/* YouTube Channel CTA */}
            <div className="mt-8 text-center">
              <a 
                href="https://www.youtube.com/@mytentor7112"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-full transition-colors"
              >
                <Youtube className="w-5 h-5" />
                <span className="font-medium">Kunjungi Channel YouTube Kami</span>
                <ArrowRight className="w-4 h-4" />
              </a>
              <p className="text-slate-500 text-sm mt-3">
                Subscribe untuk video pembelajaran dan tips sukses lainnya
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimoni Section */}
      <section id="testimonials" className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-[#4472C4]/10 text-[#4472C4] hover:bg-[#4472C4]/20 mb-4">Testimoni</Badge>
            <h2 className="text-3xl font-bold text-slate-900 mb-2">Apa Kata Mereka?</h2>
            <p className="text-slate-500">Cerita sukses alumni MyTentor yang sudah meraih impian</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {displayTestimonials.map((t, idx) => (
              <div 
                key={t.id || idx} 
                className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-lg transition-all hover:-translate-y-1"
              >
                {/* Quote Icon */}
                <div className="mb-4">
                  <Quote className="w-8 h-8 text-[#4472C4]/20" />
                </div>
                
                {/* Text */}
                <p className="text-slate-600 text-sm leading-relaxed mb-4">
                  &ldquo;{t.quote}&rdquo;
                </p>
                
                {/* Rating */}
                <div className="flex gap-0.5 mb-4">
                  {[...Array(t.rating || 5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                
                {/* Profile */}
                <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
                  <img 
                    src={t.image_url || 'https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=100&h=100&fit=crop&crop=face'} 
                    alt={t.name}
                    className="w-10 h-10 rounded-full object-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=100&h=100&fit=crop&crop=face';
                    }}
                  />
                  <div>
                    <p className="font-semibold text-slate-900 text-sm">{t.name}</p>
                    <p className="text-xs text-[#4472C4]">{t.role}</p>
                    <p className="text-xs text-slate-400">{t.institution}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* CTA for more testimonials */}
          <div className="text-center mt-8">
            <a href={`https://wa.me/${s.whatsapp_number}?text=Halo, saya ingin tahu lebih banyak tentang testimoni alumni MyTentor`} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="rounded-lg">
                Lihat Lebih Banyak Testimoni
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Ebook Premium */}
      <section id="ebook" className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-[#4472C4]/10 text-[#4472C4] hover:bg-[#4472C4]/20 mb-4">Ebook Premium</Badge>
            <h2 className="text-3xl font-bold text-slate-900">Koleksi ebook berkualitas</h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {ebooks.map((book) => (
              <div key={book.title} className="p-5 bg-white rounded-xl border border-slate-200 hover:shadow-lg transition-shadow">
                <h3 className="font-semibold text-slate-900 mb-2">{book.title}</h3>
                <p className="text-sm text-slate-500 mb-4">{book.pages} halaman</p>
                <div className="flex items-center justify-between">
                  <span className={`font-bold ${book.price === 0 ? 'text-green-600' : 'text-slate-900'}`}>
                    {formatPrice(book.price)}
                  </span>
                  <a href={`https://wa.me/${s.whatsapp_number}`} target="_blank" rel="noopener noreferrer">
                    <Button size="sm" className={book.price === 0 ? 'bg-green-600 hover:bg-green-700' : 'bg-[#4472C4] hover:bg-[#3861a8]'}>
                      {book.price === 0 ? 'Ambil' : 'Beli'}
                    </Button>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-[#4472C4]">
        <div className="max-w-3xl mx-auto text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Siap Raih Kesuksesan?</h2>
          <p className="text-lg opacity-90 mb-8">
            Bergabung dengan {dynamicStats.users} siswa yang sudah berhasil
          </p>
          <Link to="/register">
            <Button className="bg-[#FF6B35] hover:bg-[#e55a2a] text-white rounded-lg px-8 py-6 text-lg" data-testid="cta-bottom-register">
              Daftar Gratis Sekarang
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <Footer />

      {/* WhatsApp Floating Button */}
      <a 
        href={`https://wa.me/${s.whatsapp_number}?text=${encodeURIComponent(s.whatsapp_message)}`}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-xl hover:scale-110 transition-all flex items-center gap-2"
        data-testid="whatsapp-btn"
      >
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>
    </div>
  );
}
